import React, { useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  BarChart3, ChevronRight, FileText, Globe, LayoutDashboard, LogOut, Mail,
  Menu, SearchCheck, Settings, Upload, Users, X,
} from "@/lib/icons";
import BrandLogo from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { useSeo } from "@/lib/seo";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/admin", label: "Dashboard", description: "Daily operations", icon: LayoutDashboard, end: true },
      { to: "/admin/analytics", label: "Analytics", description: "Traffic & engagement", icon: BarChart3 },
      { to: "/admin/seo", label: "SEO Health", description: "Search visibility", icon: SearchCheck },
    ],
  },
  {
    label: "Community",
    items: [
      { to: "/admin/members", label: "Members", description: "Review & manage", icon: Users },
      { to: "/admin/import", label: "Import", description: "Bulk onboarding", icon: Upload },
      { to: "/admin/email", label: "Email", description: "Onboarding & campaigns", icon: Mail },
    ],
  },
  {
    label: "Website",
    items: [
      { to: "/admin/cms", label: "Content", description: "Pages & brand", icon: FileText },
      { to: "/admin/settings", label: "Settings", description: "Integrations & avatars", icon: Settings },
    ],
  },
];

const ALL_NAV = NAV_GROUPS.flatMap((group) => group.items);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  useSeo({ title: "Admin | PRONET", noindex: true });

  if (user === null) return <div className="admin-loading">Loading admin workspace…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const current = [...ALL_NAV]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)) || ALL_NAV[0];

  const Sidebar = ({ onNavigate }) => (
    <div className="flex h-full flex-col">
      <div className="admin-sidebar-brand">
        <Link to="/" className="flex min-w-0 items-center gap-3" onClick={onNavigate}>
          <BrandLogo size={36} testid="admin-sidebar-brand" />
          <div className="min-w-0">
            <div className="font-display text-[17px] font-semibold leading-none text-white">PRONET</div>
            <div className="mt-1 text-[9px] font-semibold uppercase tracking-[.24em] text-violet-300">Admin workspace</div>
          </div>
        </Link>
      </div>

      <nav className="admin-sidebar-nav" aria-label="Admin navigation">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="admin-nav-group">
            <div className="admin-nav-label">{group.label}</div>
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) => `admin-nav-item ${isActive ? "is-active" : ""}`}
                >
                  <span className="admin-nav-icon"><item.icon size={18} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{item.label}</span>
                    <span className="block truncate text-[10px] opacity-55">{item.description}</span>
                  </span>
                  <ChevronRight size={15} className="admin-nav-arrow" />
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="admin-sidebar-footer">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[.16em] text-white/35">Signed in as</div>
          <div className="mt-1 truncate text-xs font-medium text-white/70">{user.email}</div>
        </div>
        <button
          type="button"
          title="Sign out"
          data-testid="admin-logout"
          onClick={async () => { await logout(); nav("/admin/login"); }}
          className="admin-icon-button text-red-300"
        >
          <LogOut size={17} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="admin-shell min-h-screen md:grid md:grid-cols-[268px_minmax(0,1fr)]">
      <aside className="admin-sidebar hidden md:block md:sticky md:top-0 md:h-screen">
        <Sidebar />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Admin navigation">
          <button className="absolute inset-0 bg-black/70 backdrop-blur-sm" aria-label="Close menu" onClick={() => setOpen(false)} />
          <aside className="admin-sidebar absolute inset-y-0 left-0 w-[min(86vw,310px)] shadow-2xl">
            <button className="admin-icon-button absolute right-3 top-3 z-10" onClick={() => setOpen(false)} aria-label="Close menu">
              <X size={18} />
            </button>
            <Sidebar onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="min-w-0">
        <header className="admin-topbar">
          <div className="flex min-w-0 items-center gap-3">
            <button type="button" className="admin-icon-button md:hidden" onClick={() => setOpen(true)} data-testid="admin-mobile-menu" aria-label="Open menu">
              <Menu size={20} />
            </button>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-white">{current.label}</div>
              <div className="hidden text-[10px] text-white/40 sm:block">{current.description}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="admin-visit-site hidden sm:inline-flex"><Globe size={14} /> View website</Link>
            <ThemeToggle />
          </div>
        </header>

        <main className="admin-main"><Outlet /></main>
      </div>
    </div>
  );
}
